// src/utils/jwtHelper.ts
import { jwtDecode } from "jwt-decode";

export interface UserProfile {
  Id: string;
  UserName: string;
  Firstname: string;
  Lastname: string;
  Email: string;
  PhoneNumber: string;
}

export interface JwtPayload {
  CompanyId?: string | number;
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
  client_id: string;
  sub: string;
  auth_time: number;
  idp: string;
  profile: string; // JSON string
  role: string | string[];
  iat: number;
  scope: string[];
  amr: string[];
  orgRoles?: string[]; // Thêm orgRoles để hỗ trợ kiểm tra quyền admin
}

export const PROFILE_KEY = "user.profile";
export const JWT_PAYLOAD_KEY = "jwt.payload";

/**
 * Decode JWT token và trả về payload
 */
export const decodeJwtToken = (token: string): JwtPayload | null => {
  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

/**
 * Decode JWT token, parse profile và lưu vào localStorage
 */
export const saveUserProfileFromToken = (
  accessToken: string
): UserProfile | null => {
  try {
    const decoded = decodeJwtToken(accessToken);
    if (!decoded || !decoded.profile) {
      console.warn("No profile found in token");
      return null;
    }

    // Parse profile JSON string
    const profile: UserProfile = JSON.parse(decoded.profile);

    // Lưu vào localStorage
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    localStorage.setItem(JWT_PAYLOAD_KEY, JSON.stringify(decoded));
    if (decoded?.CompanyId) {
      localStorage.setItem("companyId", String(decoded.CompanyId));
    }

    return profile;
  } catch (error) {
    console.error("Error saving user profile from token:", error);
    return null;
  }
};

/**
 * Lấy user profile từ localStorage
 */
export const getUserProfile = (): UserProfile | null => {
  try {
    const profileStr = localStorage.getItem(PROFILE_KEY);
    if (!profileStr) return null;
    return JSON.parse(profileStr);
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
};

/**
 * Lấy JWT payload từ localStorage
 */
export const getJwtPayload = (): JwtPayload | null => {
  try {
    const payloadStr = localStorage.getItem(JWT_PAYLOAD_KEY);
    if (!payloadStr) return null;
    return JSON.parse(payloadStr);
  } catch (error) {
    console.error("Error getting JWT payload:", error);
    return null;
  }
};

/**
 * Decode và lấy toàn bộ thông tin từ JWT token
 */
export const getDecodedJwtToken = (token: string): JwtPayload | null => {
  return decodeJwtToken(token);
};

/**
 * Kiểm tra xem user có quyền admin trong organization không
 */
export const isUserAdmin = (): boolean => {
  try {
    const payload = getJwtPayload();
    if (!payload || !payload.orgRoles) {
      return false;
    }

    // Kiểm tra nếu orgRoles có chứa "SuperUser"
    return (
      Array.isArray(payload.orgRoles) && payload.orgRoles.includes("Admin")
    );
  } catch (error) {
    console.error("Error checking admin permission:", error);
    return false;
  }
};

/**
 * Kiểm tra xem folder hiện tại có isOrganization: true không
 * Tích hợp với Redux store để lấy thông tin folder hiện tại
 */
export const isCurrentFolderOrganization = (): boolean => {
  try {
    // Kiểm tra xem có đang ở trong team drives hoặc shared folder
    const currentPath = window.location.pathname;

    // Nếu đang ở team drives page thì luôn trả về true
    if (currentPath.includes("/page/team-drives")) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking folder organization status:", error);
    return false;
  }
};

/**
 * Kiểm tra xem có nên thêm isOrganization: true không
 * (User là admin HOẶC folder hiện tại có isOrganization: true)
 */
export const shouldAddOrganizationFlag = (): boolean => {
  return isCurrentFolderOrganization();
};

/**
 * Kiểm tra xem user có phải admin và đang ở folderRootId không
 * (dùng để hiển thị "Manage members" thay vì "Share")
 */
export const isAdminAtFolderRoot = (): boolean => {
  try {
    const isAdmin = isUserAdmin();
    const currentFolderId = localStorage.getItem("folderId");
    const folderRootId = localStorage.getItem("folderRootId");
  
    if (!isAdmin) {
      return false;
    }

    // Nếu đang ở folderRootId thì cho phép manage members
    if (currentFolderId && folderRootId && currentFolderId === folderRootId) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking admin at folder root:", error);
    return false;
  }
};

/**
 * Lấy teamId từ breadcrumbs (thằng đầu tiên không tính root)
 * Chỉ trả về teamId khi người dùng ở url page/team-drives hoặc (isUserAdmin và ở page/files)
 */
export const getTeamIdFromBreadcrumbs = (breadcrumbs: any[]): string | undefined => {
  try {
    const currentPath = window.location.pathname;
    const isAdmin = isUserAdmin();

    // Lấy thằng đầu tiên không tính root
    const firstNonRootBreadcrumb = breadcrumbs.find(bc => !bc.isRoot);
    
    return firstNonRootBreadcrumb?.id;
  } catch (error) {
    console.error("Error getting teamId from breadcrumbs:", error);
    return undefined;
  }
};

/**
 * Xóa user profile khỏi localStorage
 */
export const clearUserProfile = () => {
  localStorage.removeItem(PROFILE_KEY);
};
